import { useState, type FC, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Input } from '@/ui';

import { MANAGERS } from '../../mock/manager';
import { signIn } from './lib/session';

/*
 * Экран входа. Раскладка повторяет .login-shell макета: слева форма, справа
 * тёмная панель с акцентом.
 *
 * Проверка учётных данных - заглушка: ищем менеджера по email из справочника в
 * mock/manager.ts, пароль не проверяется. Настоящий вход появится вместе с
 * решением по ролям и аутентификации.
 */
export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const manager = MANAGERS.find(
      (candidate) =>
        candidate.email.toLowerCase() === email.trim().toLowerCase(),
    );

    if (!manager) {
      setError('Менеджер с таким email не найден.');
      return;
    }

    if (!password.trim()) {
      setError('Введите пароль.');
      return;
    }

    signIn(manager);
    void navigate({ to: '/dashboard' });
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="grid place-items-center bg-white px-6 py-12">
        <div className="w-full max-w-[420px]">
          <div className="mb-8 flex items-center gap-3">
            <div className="grid size-11 place-items-center rounded-[11px] bg-linear-to-br from-brand-green to-brand-green-700 text-lg font-bold tracking-[-0.02em] text-white">
              К
            </div>
            <div>
              <div className="text-base font-semibold text-ink-900">
                Культура
              </div>
              <div className="text-xs text-ink-500">Управление отгрузками</div>
            </div>
          </div>

          <h1 className="m-0 text-[28px] font-[650] tracking-[-0.02em] text-ink-900">
            Вход в систему
          </h1>
          <p className="mt-1.5 mb-7 text-[13px] text-ink-500">
            Рабочее место менеджера по продажам
          </p>

          <form onSubmit={submit} className="flex flex-col gap-4">
            <label htmlFor="login-email" className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-700">Email</span>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                placeholder="snejana@kultura.ru"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <label htmlFor="login-password" className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-700">Пароль</span>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>

            {error !== '' && (
              <p
                role="alert"
                className="m-0 rounded-lg border border-st-delayed-bg bg-st-delayed-bg px-3.5 py-3 text-[13px] text-st-cancelled"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-1 w-full"
            >
              Войти
            </Button>
          </form>

          <div className="mt-8 border-t border-ink-150 pt-5">
            <p className="m-0 mb-2.5 text-xs text-ink-500">
              Демо-доступ: аутентификация ещё не реализована, пароль любой.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {MANAGERS.map((manager) => (
                <Button
                  key={manager.id}
                  size="sm"
                  onClick={() => {
                    setEmail(manager.email);
                    setPassword('demo');
                    setError('');
                  }}
                >
                  {manager.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="relative hidden overflow-hidden bg-brand-navy p-12 lg:flex lg:flex-col lg:justify-between">
        <div
          aria-hidden
          className="absolute inset-0 opacity-100"
          style={{
            backgroundImage:
              'radial-gradient(900px 600px at 100% 0%, rgba(23,165,99,.22), transparent 60%), radial-gradient(700px 500px at 0% 100%, rgba(23,165,99,.10), transparent 60%)',
          }}
        />
        <div className="relative">
          <div className="text-[11px] font-semibold tracking-[0.08em] text-brand-green uppercase">
            Лакто-Юг
          </div>
          <p className="mt-4 mb-0 max-w-[420px] text-[26px] leading-snug font-[650] tracking-[-0.02em] text-white">
            Отгрузки, план и ротация культур - в одном рабочем месте
          </p>
        </div>
        <p className="relative m-0 max-w-[420px] text-[13px] leading-relaxed text-[#b6c8db]">
          Базовая версия: отгрузки, план, клиенты и товар работают на живом API,
          остальные разделы собраны на данных макета.
        </p>
      </div>
    </div>
  );
};
